/**
 * @file BillModal.js
 * @version 2.18.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-03-04
 * @modified 2026-10-28
 *
 * 2.18.0 (2026-10-28, Cam — units clear on every price): each goods line's
 * unit price says what it is per — as billed (a tonne, a kg), else its PO
 * line's unit (PriceWithUnit over qty_facts); charge lines as they were.
 *
 * 2.16.0 (2026-10-16, Cam): PO Reference and Stock show in Bill Details only
 * when they say something (the pencil beside the heading still sets them);
 * linked documents on a row of their own under the summary; the Xero status
 * (a bill entered here) at the end of the action bar, only as wide as it
 * needs; the totals under the line items, right-aligned, as the invoice and
 * PO windows.
 *
 * 2.15.0 (2026-10-15, Cam — "the buttons in the left panel are not fitting …
 * do away with the two panel modal"): ONE panel. A header (the bill, its
 * supplier, status / Queried / Stock / credit / archived pills, history,
 * Export, close), an action bar of slide-out buttons in groups that wraps at
 * any width (utils/billWindowActions — every action on every kind of bill,
 * greyed with its reason; Edit with no editor, Status on a bill keyed in Xero,
 * Find where the list is behind the window and Download with no PDF are now
 * greyed rather than hidden), then one scrolling column: Summary (details,
 * supplier, totals, Xero), Line items, the supplier's PDF previewed inline
 * (PdfCanvasPreview, loaded when scrolled to, with Attach / Replace and
 * Download beside it), linked documents, supplier credits and notes. No
 * endpoint, rule or dialog changed. The slide-outs are measured by
 * BillWindowContent, which mounts with them.
 *
 * 2.14.0 (2026-10-14, Cam): Bill Details show the bill's PO Reference and
 * whether it is Stock — this system's own, changed on any bill from the
 * pencil beside the heading (BillStockDialog; never sent to Xero), greyed
 * with the reason until their migration has run.
 *
 * 2.11.0 (2026-10-08, Cam): "Update PO from this bill…" (UpdatePoFromBillDialog)
 * — a bill sets its POs' costs only when the user chooses; greyed with the
 * reason when nothing differs or the bill cannot set a cost (voided, deleted,
 * a credit). Offered too after changing the PO links leaves a difference, and
 * said when the bill was changed in Xero and its POs were not.
 * "Update invoice from supplied qty…" (UpdateInvoiceFromSuppliedDialog) offers
 * again the invoice changes a refused save could not make.
 *
 * 2.12.0 (2026-10-08, Cam — supplier credits): a credit's window shows what
 * it relates to and where it is allocated, a bill's the credits naming it and
 * allocated to it (SupplierCreditPanel). "Allocate to bills…" on a credit /
 * "Apply a credit…" on a bill (SupplierCreditAllocationModal — in Xero first);
 * "Add credit against this bill" opens Add Supplier Credit with the supplier
 * and this bill. Every button is there on every kind, greyed with the reason.
 * 2.12.1 (2026-10-08): a credit entered here shows what it is for (Goods
 * returned / Price correction).
 * 2.12.2 (2026-10-08, review): on a supplier credit the POs button edits its
 * REFERENCES (CreditLinksDialog — POs and bills, billed POs too, starting
 * from what it names, saved whole), in Xero or not; never goods links.
 * 2.13.0 (2026-10-08, Cam): a supplier credit never changes a PO's cost —
 * "Update PO from this bill…" is greyed on a credit with that reason.
 *
 * 2.6.0 (2026-10-03): a bill entered here shows where it is on its way to
 * Xero (BillPushStatus — sent / not yet / retrying / failed with the reason,
 * and Retry); each line names the PO it bills.
 * 2.7.0 (2026-10-05): a Status button (BillStatusDialog) for bills entered
 * here — status and Queried, which on a bill in Xero reach Xero.
 * 2.8.0 (2026-10-05): a VOIDED bill's Edit and Status open the voided window
 * (VoidedDocumentDialog — raise a copy, or email accounts).
 * 2.9.0 (2026-10-05, Cam — bill 0808140549 could not take its PDF): on ANY
 * bill the parts this system keeps for itself can be changed — Attach PDF /
 * Replace PDF, Notes, and the POs it is linked to. Closing refreshes the list
 * behind.
 * 2.10.0 (2026-10-05): Edit follows the server's edit_context.
 *
 * 2.17.0 (2026-10-20, Cam): each line's quantity in its own unit with the
 * tonnes beneath (QtyWithTonnes, from the server's qty_facts — a bill per
 * tonne or per kg says so), and an "On the PO" column with the PO line's
 * supplied (else ordered) quantity the same way, so the two can be compared.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './BillModal.css';
import './TableStyles.css';
import LoadingSpinner from './LoadingSpinner';
import AuditHistoryModal from './AuditHistoryModal';
import AlertDialog from './AlertDialog';
import LinkedDocuments from './LinkedDocuments';
import { formatCurrency, formatPrice } from '../utils/currencyFormatter';
import PriceWithUnit from './PriceWithUnit';
import { isChargeBillLine } from '../utils/chargeLines';
import QtyWithTonnes from './QtyWithTonnes';
import { formatDateShort } from '../utils/dateFormatters';
import { getTaxPercentDisplay } from '../utils/taxTypes';
import { useSlideOutButton } from './SlideOutButton';
import useOpenInSearch from '../hooks/useOpenInSearch';
import DocumentOutputModal from './DocumentOutputModal';
import PdfCanvasPreview from './PdfCanvasPreview';
import BillPushStatus from './BillPushStatus';
import BillStatusDialog from './BillStatusDialog';
import { billStatusLabel } from '../utils/billStatus';
import VoidedDocumentDialog from './VoidedDocumentDialog';
import { isVoided } from '../utils/voidedDocument';
import ConfirmDialog from './ConfirmDialog';
import BillNotesDialog from './BillNotesDialog';
import BillStockDialog from './BillStockDialog';
import BillPoLinksDialog from './BillPoLinksDialog';
import { attachmentOutcome, replacePdfQuestion } from '../utils/billLocalEdits';
import UpdatePoFromBillDialog from './UpdatePoFromBillDialog';
import UpdateInvoiceFromSuppliedDialog from './UpdateInvoiceFromSuppliedDialog';
import SupplierCreditPanel from './SupplierCreditPanel';
import SupplierCreditAllocationModal from './SupplierCreditAllocationModal';
import CreditLinksDialog from './CreditLinksDialog';
import { isSupplierCredit, addCreditRowBlock, startSupplierCredit, creditKindOf, creditKindLabel } from '../utils/supplierCredits';
import { billWindowActions, actionsInGroup, BILL_ACTION_GROUPS } from '../utils/billWindowActions';

const KIND_WORDS = { CREDIT: 'Credit', OVERPAYMENT: 'Overpayment', PREPAYMENT: 'Prepayment' };

const STATUS_CLASS = {
  DRAFT: 'draft', SUBMITTED: 'submitted', AUTHORISED: 'authorised', PAID: 'billed', VOIDED: 'deleted', DELETED: 'deleted',
};
const statusBadgeClass = (status) => `status-badge ${STATUS_CLASS[String(status || '').toUpperCase()] || 'default'}`;

/** The window's title: "Bill 0808140548", "Credit CN-12", else the number / id. */
export function billWindowTitle(bill, billId) {
  if (!bill) return 'Loading…';
  const word = isSupplierCredit(bill) ? (KIND_WORDS[String(bill.transaction_type).toUpperCase()] || 'Credit') : 'Bill';
  return bill.supplier_reference ? `${word} ${bill.supplier_reference}` : (bill.bill_number || `${word} #${billId}`);
}

/** One slide-out button of the action bar (or the PDF section). */
function ActionButton({ action, onAction }) {
  return (
    <button
      type="button"
      className="btn-slideout"
      data-action={action.key}
      {...(action.writes ? { 'data-write-action': true } : {})}
      onClick={() => onAction(action)}
      disabled={action.disabled}
      title={action.title}
    >
      <div className="icon-wrapper">
        <span className={`material-symbols-rounded${action.spinning ? ' spinning' : ''}`}>{action.icon}</span>
      </div>
      <span className="btn-label">{action.label}</span>
      {action.badge ? <span className="btn-badge">{action.badge}</span> : null}
    </button>
  );
}

/**
 * Renders its children once scrolled near — so opening a bill does not fetch
 * and draw its PDF until someone looks at it (as the old Attachment panel
 * waited to be opened). Without IntersectionObserver it renders at once.
 */
function WhenNearView({ children }) {
  const ref = useRef(null);
  const [near, setNear] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (near || !ref.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); io.disconnect(); }
    }, { rootMargin: '300px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [near]);
  return <div ref={ref} className="bill-pdf-slot">{near ? children : <div className="bill-pdf-waiting"><LoadingSpinner /></div>}</div>;
}

/**
 * Everything below the header, once the bill has loaded. It mounts with the
 * action bar's slide-out buttons, so the hook that sizes them is called here
 * (CLAUDE.md) — not in BillModal, whose first render is the spinner.
 */
function BillWindowContent({
  bill, billId, actions, onAction, sendingEnabled, onPushChange, onLinkedChange,
  onEditStock, onEditNotes, pdfInputRef, onPdfChosen,
}) {
  useSlideOutButton([actions.map((a) => `${a.key}:${a.label}:${a.disabled ? 1 : 0}:${a.badge || ''}`).join('|')]);

  const balance = parseFloat(bill.total) - parseFloat(bill.amount_paid || 0);
  const isPaid = Math.abs(balance) < 0.01;
  const isOverdue = !isPaid && bill.due_date && new Date(bill.due_date) < new Date();
  const lines = bill.lines || [];
  const pdfUrl = `${API_URL}/api/bills/${billId}/attachment`;
  const barGroups = BILL_ACTION_GROUPS.filter((g) => g.key !== 'pdf');

  return (
    <>
      {/* ── Action bar: every action, grouped, wrapping at any width; a bill
          entered here also shows where it is on its way to Xero, at the end
          of the bar and only as wide as it needs (Cam, 2026-10-16). The
          groups wrap among themselves, so a label sliding out never pushes
          the Xero status along or under. ── */}
      <div className="bill-window-actions">
        <div className="bill-action-groups" role="toolbar" aria-label="Bill actions">
          {barGroups.map((group) => (
            <div key={group.key} className="buttons slideout bill-action-group" data-group={group.key} aria-label={group.label}>
              {actionsInGroup(actions, group.key).map((action) => (
                <div key={action.key}><ActionButton action={action} onAction={onAction} /></div>
              ))}
            </div>
          ))}
        </div>
        {bill.source === 'local' && (
          <div className="bill-xero-status" aria-label="Xero">
            <BillPushStatus
              billId={bill.id}
              compact
              status={{
                source: bill.source,
                push_status: bill.xero_push_status ?? null,
                xero_bill_id: bill.xero_bill_id || null,
                xero_url: bill.xero_url || null,
                error: bill.xero_push_error || null,
                attempts: bill.xero_push_attempts || 0,
                info: bill.xero_push_info || {},
                sending_enabled: sendingEnabled,
              }}
              onChange={onPushChange}
            />
          </div>
        )}
      </div>

      <div className="bill-window-body">
        {/* ── Summary ── */}
        <section className="bill-window-section bill-summary" aria-label="Summary">
          <div className="bill-summary-grid">
            <div className="detail-section bill-summary-details">
              <div className="bill-section-heading">
                <h3>Bill Details</h3>
                {/* PO Reference and Stock — this system's own, on any bill. */}
                <button className="btn-icon" data-write-action onClick={onEditStock}
                        disabled={!bill.local_fields_ready}
                        title={bill.local_fields_ready
                          ? 'Edit the PO Reference and Stock (kept here only — never sent to Xero)'
                          : 'PO Reference and Stock are not switched on yet — the database update for them has not been run.'}>
                  <span className="material-symbols-rounded">inventory_2</span>
                </button>
              </div>
              <div className="detail-grid bill-detail-columns">
                {bill.supplier_reference && (
                  <div className="detail-row"><span className="label">Reference</span><span className="value">{bill.supplier_reference}</span></div>
                )}
                <div className="detail-row"><span className="label">Bill Date</span><span className="value">{formatDateShort(bill.bill_date) || '-'}</span></div>
                <div className="detail-row">
                  <span className="label">Due Date</span>
                  <span className={`value${isOverdue ? ' is-overdue' : ''}`}>{formatDateShort(bill.due_date) || '-'}{isOverdue && ' (Overdue)'}</span>
                </div>
                {/* Only when they say something (Cam, 2026-10-16); the
                    pencil above sets them either way. */}
                {bill.local_fields_ready && bill.po_reference && (
                  <div className="detail-row"><span className="label">PO Reference</span><span className="value">{bill.po_reference}</span></div>
                )}
                {bill.local_fields_ready && bill.is_stock && (
                  <div className="detail-row">
                    <span className="label">Stock</span>
                    <span className="value"><span className="stock-badge">Stock</span></span>
                  </div>
                )}
                {bill.reference && (
                  <div className="detail-row"><span className="label">Our Reference</span><span className="value">{bill.reference}</span></div>
                )}
                <div className="detail-row"><span className="label">Source</span><span className="value capitalise">{bill.source}</span></div>
                {bill.transaction_type && bill.transaction_type !== 'BILL' && (
                  <div className="detail-row">
                    <span className="label">Type</span>
                    <span className="value">{bill.transaction_type.charAt(0) + bill.transaction_type.slice(1).toLowerCase()}</span>
                  </div>
                )}
                {creditKindOf(bill) && (
                  <div className="detail-row"><span className="label">Credit is for</span><span className="value">{creditKindLabel(creditKindOf(bill))}</span></div>
                )}
                {bill.is_queried && (
                  <div className="detail-row"><span className="label">Queried</span><span className="value">Yes — raised with the supplier</span></div>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>Supplier</h3>
              <div className="detail-grid">
                <div className="detail-row"><span className="label">Name</span><span className="value">{bill.supplier_name || bill.account_company_name || '-'}</span></div>
                {bill.account_no && (
                  <div className="detail-row"><span className="label">Account No</span><span className="value">{bill.account_no}</span></div>
                )}
                {bill.delivery_line1 && (
                  <div className="detail-row">
                    <span className="label">Address</span>
                    <span className="value address-value">
                      {[bill.delivery_line1, bill.delivery_line2, bill.delivery_city, bill.delivery_region, bill.delivery_postcode, bill.delivery_country]
                        .filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </section>

        {/* Its POs, their customer orders and invoices, other bills on the
            same POs — the shared panel the order, invoice and PO windows carry,
            on a row of its own under the summary, as the invoice window has it
            (Cam, 2026-10-16). */}
        <LinkedDocuments type="bill" id={bill.id} refreshKey={bill} onChange={onLinkedChange} />

        {/* ── Line items ── */}
        <section className="bill-window-section detail-section" aria-label="Line items">
          <h3>Line items{lines.length ? ` (${lines.length})` : ''}</h3>
          {lines.length > 0 ? (
            <div className="table-container">
              <table className="data-table bill-lines-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Item Code</th>
                    <th className="text-right">Qty</th>
                    {lines.some(l => l.qty_facts?.po_line_quantity != null) && (
                      <th className="text-right" title="The PO line this bills — supplied where recorded, else ordered — in its own unit and tonnes">On the PO</th>
                    )}
                    <th className="text-right">Unit Amount</th>
                    <th className="text-right">Line Amount</th>
                    <th className="col-tax">Tax %</th>
                    <th className="text-right">Account Code</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={line.id || idx}>
                      {/* Catalogue name, then the line's own wording where it
                          says something the name does not, then the PO it
                          bills — the shape the PO and invoice line tables use. */}
                      <td>
                        <span className="bill-line-name">{line.item_name || line.description || '-'}</span>
                        {line.description && line.item_name
                          && !String(line.description).toLowerCase().includes(String(line.item_name).toLowerCase())
                          && <span className="bill-line-desc">{line.description}</span>}
                        {line.purchase_order_number && (
                          <span className="bill-line-desc">On {line.purchase_order_number}</span>
                        )}
                      </td>
                      <td>{line.item_code || '-'}</td>
                      {/* In its own unit with the tonnes it comes to
                          (utils/qtyWithTonnes — Cam, 20 Oct 2026): a bill per
                          tonne or per kg says so, one in the PO's units reads
                          as the PO line counts it. */}
                      <td className="text-right">
                        <QtyWithTonnes quantity={line.quantity} line={line.qty_facts || {}} />
                        {/* A unit the invoice printed that is none of ours. */}
                        {line.unit && !line.qty_facts?.qty_unit && String(line.unit).toLowerCase() !== 'pack'
                          && <span className="bill-line-unit">{line.unit}</span>}
                      </td>
                      {lines.some(l => l.qty_facts?.po_line_quantity != null) && (
                        <td className="text-right">
                          {line.qty_facts?.po_line_quantity != null ? (() => {
                            const f = line.qty_facts;
                            const supplied = f.po_line_supplied_quantity != null;
                            return (
                              <QtyWithTonnes
                                quantity={supplied ? f.po_line_supplied_quantity : f.po_line_quantity}
                                line={{ ...f, qty_unit: f.po_line_qty_unit, pallets: null }}
                                label={supplied ? 'Supplied' : 'Ordered'}
                              />
                            );
                          })() : '-'}
                        </td>
                      )}
                      <td className="text-right">{isChargeBillLine(line)
                        ? formatPrice(line.unit_amount)
                        : <PriceWithUnit value={line.unit_amount} line={line.qty_facts || {}} />}</td>
                      <td className="text-right">{formatCurrency(line.line_amount)}</td>
                      <td className="col-tax">{getTaxPercentDisplay(line.tax_type)}</td>
                      <td className="text-right">{line.account_code || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="bill-window-empty">No line items.</p>
          )}

          {/* Totals under the lines, right-aligned — as the invoice and PO
              windows (Cam, 2026-10-16). */}
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row"><span className="label">Subtotal:</span><span className="value">{formatCurrency(bill.sub_total)}</span></div>
              <div className="total-row"><span className="label">VAT:</span><span className="value">{formatCurrency(bill.total_tax)}</span></div>
              <div className="total-row total"><span className="label">Total:</span><span className="value">{formatCurrency(bill.total)}</span></div>
              {parseFloat(bill.amount_paid) > 0 && (
                <>
                  <div className="total-row payments"><span className="label">Less Payments:</span><span className="value">({formatCurrency(bill.amount_paid)})</span></div>
                  <div className="total-row balance">
                    <span className="label">Balance Due:</span>
                    <span className={`value ${balance > 0 ? 'balance-owing' : 'balance-clear'}`}>{formatCurrency(Math.max(0, balance))}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        {/* ── The supplier's PDF, inline ── */}
        <section className="bill-window-section detail-section bill-attachment" aria-label="Supplier's PDF">
          <div className="bill-section-heading">
            <h3>Supplier's PDF</h3>
            <div className="buttons slideout bill-pdf-actions">
              {actionsInGroup(actions, 'pdf').map((action) => (
                <div key={action.key}><ActionButton action={action} onAction={onAction} /></div>
              ))}
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                className="bill-pdf-input"
                data-testid="bill-pdf-input"
                onChange={(e) => onPdfChosen(e.target.files && e.target.files[0])}
              />
            </div>
          </div>
          {bill.attachment_path ? (
            <div className="bill-pdf-box">
              <div className="bill-pdf-header">
                <span className="material-symbols-rounded">picture_as_pdf</span>
                <span className="bill-pdf-title">{bill.attachment_filename || 'Attachment'}</span>
                <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-icon" title="Open in new tab">
                  <span className="material-symbols-rounded">open_in_new</span>
                </a>
              </div>
              <WhenNearView>
                <PdfCanvasPreview
                  className="bill-pdf-frame"
                  loadKey={`${pdfUrl}#${bill.attachment_path}`}
                  load={() => fetch(pdfUrl, { credentials: 'include' }).then((r) => { if (!r.ok) throw new Error('Could not load the PDF'); return r.arrayBuffer(); })}
                />
              </WhenNearView>
            </div>
          ) : (
            <p className="bill-window-empty">No PDF attached yet.</p>
          )}
        </section>

        {/* Supplier credits: what a credit relates to and where it is
            allocated; the credits naming / allocated to a bill. */}
        <SupplierCreditPanel bill={bill} refreshKey={bill} />

        {/* ── Notes — this system's own, editable on any bill ── */}
        <section className="bill-window-section detail-section" aria-label="Notes">
          <div className="bill-section-heading">
            <h3>Notes</h3>
            <button className="btn-icon" data-write-action onClick={onEditNotes}
                    title="Edit the notes (kept here only — never sent to Xero)">
              <span className="material-symbols-rounded">edit_note</span>
            </button>
          </div>
          {bill.notes
            ? <p className="bill-notes">{bill.notes}</p>
            : <p className="bill-notes bill-notes-empty">No notes.</p>}
        </section>
      </div>
    </>
  );
}

function BillModal({ billId, onClose, onEdit, onRefresh, onOpenInSearch, onNavigate }) {
  // "Find in …" wherever this was opened from (hooks/useOpenInSearch).
  const { open: openInSearch } = useOpenInSearch('bill', onOpenInSearch, onClose);
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Is sending bills to Xero switched on? For the push status's wording.
  const [sendingEnabled, setSendingEnabled] = useState(null);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  // Export (DocumentOutputModal): preview, print, download, email to yourself.
  const [exporting, setExporting] = useState(false);
  // Status and Queried (BillStatusDialog).
  const [changingStatus, setChangingStatus] = useState(false);
  // A voided bill someone tried to edit or change (VoidedDocumentDialog).
  const [showVoided, setShowVoided] = useState(false);
  // This system's own parts of the bill, changeable on any bill: the PDF,
  // notes and PO links. `changed` makes closing refresh the list behind.
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [confirmReplacePdf, setConfirmReplacePdf] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editingStock, setEditingStock] = useState(false);
  const [editingPos, setEditingPos] = useState(false);
  // Update PO from this bill… and Update invoice from supplied qty… (2026-10-08):
  // what differs, read with the bill, so the buttons can say why they are greyed.
  const [poCostInfo, setPoCostInfo] = useState(null);       // { count, block }
  const [invoiceInfo, setInvoiceInfo] = useState(null);     // { count }
  const [updatingPo, setUpdatingPo] = useState(false);
  const [updatingInvoice, setUpdatingInvoice] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const changedRef = useRef(false);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/bills/push-settings`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled && d.success) setSendingEnabled(d.enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (billId) {
      fetchBillDetails();
      document.body.classList.add('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.add('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billId]);

  // quiet: refresh in place (after a linked document opened from here closes)
  // rather than swapping the whole window for a spinner.
  const fetchBillDetails = async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bills/${billId}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setBill(data.bill);
    } catch (error) {
      console.error('Failed to fetch bill:', error);
    } finally {
      setLoading(false);
    }
    loadDifferences();
  };

  // What differs between the bill and its POs on cost, and the invoice
  // changes its supplied quantities still call for. Never in the way: a read
  // that fails leaves the buttons open, and their windows say.
  const loadDifferences = () => {
    fetch(`${API_URL}/api/bills/${billId}/po-cost-differences`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setPoCostInfo(d.success ? { count: d.differences?.length || 0, block: d.block || null } : null))
      .catch(() => setPoCostInfo(null));
    fetch(`${API_URL}/api/bills/${billId}/invoice-proposals`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setInvoiceInfo(d.success ? { count: d.proposals?.length || 0 } : null))
      .catch(() => setInvoiceInfo(null));
  };

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      document.body.classList.remove('modal-open');
      const cw = document.querySelector('.content-wrapper');
      if (cw) cw.classList.remove('modal-open');
      // Something changed here: the row behind is stale (CLAUDE.md).
      if (changedRef.current && onRefresh) onRefresh();
      onClose();
    }, 300);
  };

  // Something this window changed: show it now, refresh the list on close.
  const afterLocalChange = () => {
    changedRef.current = true;
    fetchBillDetails({ quiet: true });
  };

  // Attach PDF / Replace PDF — any bill, any status (POST /:id/attachment).
  const choosePdf = () => {
    if (bill?.attachment_path) { setConfirmReplacePdf(true); return; }
    if (pdfInputRef.current) pdfInputRef.current.click();
  };
  const uploadPdf = async (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setAlertDialog({ isOpen: true, type: 'warning', message: 'Only PDF files can be attached.' });
      return;
    }
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/bills/${billId}/attachment?extract=false`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || 'The PDF could not be saved');
      const outcome = attachmentOutcome(data);
      setAlertDialog({ isOpen: true, type: outcome.type, message: outcome.text });
      afterLocalChange();
    } catch (err) {
      setAlertDialog({ isOpen: true, type: 'error', message: `The PDF was not saved: ${err.message}` });
    } finally {
      setUploadingPdf(false);
      if (pdfInputRef.current) pdfInputRef.current.value = '';
    }
  };

  const voided = !!bill && isVoided(bill.status);
  const actions = billWindowActions(bill, {
    hasEditor: typeof onEdit === 'function',
    canFind: typeof openInSearch === 'function',
    canNavigate: typeof onNavigate === 'function',
    poCostInfo,
    invoiceInfo,
    uploadingPdf,
  });

  // One place every button of the window goes through. A greyed button cannot
  // be pressed; the voided ones open the voided window instead.
  const runAction = (action) => {
    if (!bill || (action.disabled && !action.opensVoided)) return;
    if (action.opensVoided) { setShowVoided(true); return; }
    switch (action.key) {
      case 'edit':
        if (onEdit) { handleClose(); onEdit(bill.id); }
        return;
      case 'status': setChangingStatus(true); return;
      case 'pos': setEditingPos(true); return;
      case 'updatePo': setUpdatingPo(true); return;
      case 'updateInvoice': setUpdatingInvoice(true); return;
      case 'allocate': setAllocating(true); return;
      case 'addCredit':
        if (!addCreditRowBlock(bill) && onNavigate) {
          handleClose();
          startSupplierCredit(onNavigate, {
            account_id: bill.account_id,
            xero_contact_id: bill.xero_contact_id,
            supplier_name: bill.supplier_name || bill.account_company_name,
            bill: { id: bill.id, supplier_reference: bill.supplier_reference || bill.bill_number },
          });
        }
        return;
      case 'find': if (openInSearch) openInSearch(bill); return;
      case 'attachPdf': choosePdf(); return;
      case 'downloadPdf': window.open(`${API_URL}/api/bills/${billId}/attachment`, '_blank'); return;
      default:
    }
  };

  if (!billId) return null;

  const credit = isSupplierCredit(bill);
  const balance = bill ? parseFloat(bill.total) - parseFloat(bill.amount_paid || 0) : 0;
  const isOverdue = !!bill && Math.abs(balance) >= 0.01 && bill.due_date && new Date(bill.due_date) < new Date();
  const title = loading ? 'Loading…' : billWindowTitle(bill, billId);

  return createPortal(
    <>
      <div className={`modal-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
        <div
          className={`modal-content bill-window ${closing ? 'closing' : ''}`}
          role="dialog"
          aria-label={title}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="bill-window-header">
            <div className="bill-window-title">
              <div className="bill-window-heading">
                <h2>{title}</h2>
                {!loading && bill && (
                  <div className="bill-window-badges">
                    <span className={statusBadgeClass(bill.status)}>{billStatusLabel(bill.status)}</span>
                    {isOverdue && <span className="status-badge overdue">Overdue</span>}
                    {bill.is_queried && (
                      <span className="status-badge bill-pill-queried" title="Queried — raised with the supplier">
                        <span className="material-symbols-rounded">help</span>Queried
                      </span>
                    )}
                    {bill.is_stock && <span className="stock-badge">Stock</span>}
                    {credit && (
                      <span className="status-badge bill-pill-kind">{KIND_WORDS[String(bill.transaction_type).toUpperCase()] || 'Credit'}</span>
                    )}
                    {bill.is_archived && <span className="status-badge bill-pill-archived">Archived</span>}
                  </div>
                )}
              </div>
              {!loading && bill && (
                <div className="bill-window-subtitle">
                  {bill.supplier_name || bill.account_company_name || 'No supplier'}
                  {bill.bill_number && bill.supplier_reference && bill.bill_number !== bill.supplier_reference
                    ? ` · ${bill.bill_number}` : ''}
                  {` · ${formatCurrency(bill.total)}`}
                </div>
              )}
            </div>
            <div className="bill-window-header-icons">
              <button className="btn-icon" onClick={() => setShowHistory(true)} disabled={loading} title="View history">
                <span className="material-symbols-rounded">history</span>
              </button>
              <button className="btn-icon" data-read-safe onClick={() => setExporting(true)} disabled={loading || !bill?.id}
                      title="Export — preview, then print, download or email to yourself">
                <span className="material-symbols-rounded">ios_share</span>
              </button>
              <button className="btn-icon close-btn" onClick={handleClose} title="Close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
          </div>

          {loading ? (
            <div className="bill-window-loading"><LoadingSpinner /></div>
          ) : !bill ? (
            <div className="bill-window-loading no-data">Bill not found</div>
          ) : (
            <BillWindowContent
              bill={bill}
              billId={billId}
              actions={actions}
              onAction={runAction}
              sendingEnabled={sendingEnabled}
              onPushChange={() => { fetchBillDetails({ quiet: true }); onRefresh && onRefresh(); }}
              onLinkedChange={() => fetchBillDetails({ quiet: true })}
              onEditStock={() => setEditingStock(true)}
              onEditNotes={() => setEditingNotes(true)}
              pdfInputRef={pdfInputRef}
              onPdfChosen={uploadPdf}
            />
          )}
        </div>
      </div>

      {exporting && bill?.id && (
        <DocumentOutputModal
          type="bill"
          ids={[bill.id]}
          onClose={() => setExporting(false)}
          onDone={(message) => { setExporting(false); if (message) setAlertDialog({ isOpen: true, message, type: 'success' }); }}
        />
      )}

      {showVoided && bill && (
        <VoidedDocumentDialog
          kind="bill"
          id={bill.id}
          onClose={() => setShowVoided(false)}
          onNavigate={onNavigate ? (menu) => { handleClose(); onNavigate(menu); } : null}
          onSent={(message) => setAlertDialog({ isOpen: true, type: 'success', message })}
        />
      )}

      {editingStock && bill && (
        <BillStockDialog
          bill={bill}
          onClose={() => setEditingStock(false)}
          onSaved={() => afterLocalChange()}
        />
      )}
      {editingNotes && bill && (
        <BillNotesDialog
          bill={bill}
          onClose={() => setEditingNotes(false)}
          onSaved={() => afterLocalChange()}
        />
      )}

      {editingPos && bill && String(bill.transaction_type || '').toUpperCase() === 'CREDIT' && (
        <CreditLinksDialog
          credit={bill}
          onClose={() => { setEditingPos(false); fetchBillDetails({ quiet: true }); }}
          onSaved={() => afterLocalChange()}
        />
      )}

      {editingPos && bill && String(bill.transaction_type || '').toUpperCase() !== 'CREDIT' && (
        <BillPoLinksDialog
          bill={bill}
          onClose={() => { setEditingPos(false); fetchBillDetails({ quiet: true }); }}
          onSaved={(data) => {
            afterLocalChange();
            const notes = [data?.quantityNote,
              data?.costDiffers ? `This bill now differs from its PO${data.costDiffers === 1 ? '' : 's'} on cost (${data.costDiffers} line${data.costDiffers === 1 ? '' : 's'}). The PO is not changed unless you choose — use "Update PO from this bill…".` : null
            ].filter(Boolean);
            if (notes.length) setAlertDialog({ isOpen: true, type: 'warning', message: notes.join('\n\n') });
          }}
        />
      )}

      {updatingPo && bill && (
        <UpdatePoFromBillDialog
          billId={bill.id}
          onClose={() => { setUpdatingPo(false); loadDifferences(); }}
          onDone={(message) => { afterLocalChange(); setAlertDialog({ isOpen: true, type: 'success', message }); }}
        />
      )}

      {allocating && bill && (
        <SupplierCreditAllocationModal
          docId={bill.id}
          onClose={() => { setAllocating(false); afterLocalChange(); }}
          onDone={(message) => { changedRef.current = true; setAlertDialog({ isOpen: true, type: 'success', message }); }}
        />
      )}

      {updatingInvoice && bill && (
        <UpdateInvoiceFromSuppliedDialog
          billId={bill.id}
          onClose={() => { setUpdatingInvoice(false); loadDifferences(); }}
          onDone={(message, type) => { changedRef.current = true; setAlertDialog({ isOpen: true, type: type || 'success', message }); }}
          onNavigate={onNavigate ? (menu) => { handleClose(); onNavigate(menu); } : null}
        />
      )}

      <ConfirmDialog
        isOpen={confirmReplacePdf}
        title="Replace the PDF?"
        message={replacePdfQuestion(bill)}
        confirmText="Choose a PDF"
        cancelText="Keep it"
        type="info"
        onConfirm={() => { setConfirmReplacePdf(false); if (pdfInputRef.current) pdfInputRef.current.click(); }}
        onCancel={() => setConfirmReplacePdf(false)}
      />

      {changingStatus && bill && (
        <BillStatusDialog
          bill={bill}
          onClose={() => { setChangingStatus(false); fetchBillDetails({ quiet: true }); }}
          onChanged={(data) => {
            const w = data?.warning || data?.status_change?.xero?.warning;
            setAlertDialog({
              isOpen: true,
              type: w ? 'warning' : 'success',
              message: w || `Status saved: ${data?.bill?.status || ''}${data?.bill?.is_queried ? ' (queried)' : ''}.`
            });
            if (onRefresh) onRefresh();
          }}
        />
      )}

      <AuditHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        entityType="bill"
        entityId={bill?.id}
        entityIdentifier={bill?.supplier_reference || bill?.bill_number}
      />

      <AlertDialog isOpen={alertDialog.isOpen} message={alertDialog.message} type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })} />
    </>,
    document.body
  );
}

export default BillModal;
